import { api } from './client';

export type TierName = 'Bronze' | 'Silver' | 'Gold';

export interface SeasonThreshold {
  name: TierName;
  min: number;
}

export interface SeasonStanding {
  rank: number;
  leaderboard_name: string;
  points: number;
  wins: number;
  tier: TierName | null;
}

/** A single signed-in player's standing within the open season. */
export interface PlayerSeason {
  points: number;
  wins: number;
  rank: number | null;
  tier: TierName | null;
  next: { name: TierName; min: number } | null;
  progressPct: number;
}

export interface CurrentSeasonView {
  /** 'YYYY-MM' */
  key: string;
  first: string;
  last: string;
  days_remaining: number;
  /** Ascending by `min`. */
  thresholds: SeasonThreshold[];
  total_players: number;
  standings: SeasonStanding[];
  /** The handful of players either side of the viewer, when they rank below
   *  the visible top block. Empty when they are already in `standings`,
   *  haven't played, or are signed out — at a few thousand players the rows
   *  worth showing are the ones they can actually overtake. */
  neighbours: SeasonStanding[];
  /** Null for a signed-out visitor, and for a signed-in player who hasn't
   *  played this season — both read as "no standing yet". */
  me: PlayerSeason | null;
}

export async function fetchCurrentSeason(): Promise<CurrentSeasonView> {
  const res = await api.get('/api/seasons/current');
  return res.data;
}

/** A single Platinum winner, as the server now hands them back — enough
 *  to display without a second lookup (and, for Task 12, to link to a
 *  profile via `user_id`). */
export interface PlatinumWinner {
  user_id: string;
  leaderboard_name: string;
}

/** A closed (or not-yet-closed) past month. Carries `me` and `neighbours`
 *  like the open season: without them, someone who finished 30th in June
 *  could not find their own result anywhere. */
export interface PastSeasonView {
  /** 'YYYY-MM' */
  key: string;
  first: string;
  last: string;
  closed: boolean;
  /** This season's Platinum winners, frozen at close, in rank order —
   *  empty when the season is still open, was never closed, or fell under
   *  the minimum-participants guard. Derived server-side from the SAME
   *  ranked query `standings` uses (season.service.ts's
   *  `getRankedSeasonUsers`), so this is authoritative on its own — never
   *  re-derive it by slicing `standings` client-side. */
  platinum: PlatinumWinner[];
  total_players: number;
  standings: SeasonStanding[];
  neighbours: SeasonStanding[];
  me: PlayerSeason | null;
}

export async function fetchSeason(key: string): Promise<PastSeasonView> {
  const res = await api.get(`/api/seasons/${key}`);
  return res.data;
}

/** Matches the server's STANDINGS_PAGE_SIZE (server/src/routes/seasons.ts). */
export const STANDINGS_PAGE_SIZE = 25;

export interface SeasonStandingsPage {
  total: number;
  page: number;
  rows: SeasonStanding[];
}

export async function fetchSeasonStandings(key: string, page = 1): Promise<SeasonStandingsPage> {
  const res = await api.get(`/api/seasons/${key}/standings`, { params: { page } });
  return res.data;
}

/** Every season key with at least one finished life, newest first. */
export async function fetchSeasonsList(): Promise<{ seasons: string[] }> {
  const res = await api.get('/api/seasons');
  return res.data;
}
